/*
  EmailSettings.jsx
  Provider-based Email configuration for reminders + invoice sends.
  Supports two providers: SMTP (Gmail, Zoho, etc.) and AWS SES (recommended for absolin.com domain).
*/
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { Mail, Save, Loader2, Cloud, Server, Send } from "lucide-react";

const EMPTY = {
  email_provider: "smtp",
  smtp: {
    host: "", port: 587, username: "", password: "",
    use_tls: true, use_ssl: false,
    from_email: "", from_name: "Absolin Accounts", reply_to: "",
  },
  ses: {
    aws_access_key_id: "", aws_secret_access_key: "",
    region: "ap-south-1",
    from_email: "", from_name: "Absolin Accounts",
    reply_to: "", configuration_set: "",
  },
};

const AWS_REGIONS = [
  { value: "ap-south-1", label: "Asia Pacific (Mumbai) — ap-south-1" },
  { value: "us-east-1", label: "US East (N. Virginia) — us-east-1" },
  { value: "us-west-2", label: "US West (Oregon) — us-west-2" },
  { value: "eu-west-1", label: "Europe (Ireland) — eu-west-1" },
  { value: "eu-central-1", label: "Europe (Frankfurt) — eu-central-1" },
  { value: "ap-southeast-1", label: "Asia Pacific (Singapore) — ap-southeast-1" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo) — ap-northeast-1" },
];

export default function EmailSettings() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/collections/reminders/settings");
      setForm({
        email_provider: r.data.email_provider || "smtp",
        smtp: { ...EMPTY.smtp, ...(r.data.smtp || {}) },
        ses: { ...EMPTY.ses, ...(r.data.ses || {}) },
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setProvider = (p) => setForm(f => ({ ...f, email_provider: p }));
  const setSmtp = (k, v) => setForm(f => ({ ...f, smtp: { ...f.smtp, [k]: v } }));
  const setSes = (k, v) => setForm(f => ({ ...f, ses: { ...f.ses, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      // Preserve existing whatsapp + scheduler settings
      const cur = (await api.get("/collections/reminders/settings")).data || {};
      const payload = {
        email_provider: form.email_provider,
        smtp: form.smtp,
        ses: form.ses,
        whatsapp: cur.whatsapp || { enabled: false, access_token: "", phone_number_id: "", waba_id: "", api_version: "v21.0" },
        scheduler_enabled: cur.scheduler_enabled || false,
        default_send_hour_ist: cur.default_send_hour_ist || 10,
      };
      await api.post("/collections/reminders/settings", payload);
      toast.success("Email settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Enter a recipient email for the test"); return; }
    setTesting(true);
    try {
      // Save first so the test uses the latest config
      await save();
      const r = await api.post("/collections/reminders/settings/test-email", { to_email: testTo });
      toast.success(`Test email sent via ${r.data.provider?.toUpperCase() || "email provider"} ✔`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    setTesting(false);
  };

  if (loading) return <div className="py-8 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  const isSES = form.email_provider === "aws_ses";

  return (
    <Card data-testid="email-settings-panel" className={saving ? "opacity-70 pointer-events-none" : ""}>
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3 pb-3 border-b">
          <div className="p-2 rounded-lg bg-blue-100">
            <Mail className="w-5 h-5 text-[#0066CC]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Email Configuration</h3>
            <p className="text-[10.5px] text-slate-500 max-w-md">Provider used by invoice-send + auto-reminder emails. AWS SES is recommended for the Absolin domain.</p>
          </div>
        </div>

        {/* Provider toggle */}
        <div>
          <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Email Provider</Label>
          <div className="flex gap-2 mt-1">
            <button type="button" onClick={() => setProvider("smtp")} className={`flex-1 border rounded-lg px-3 py-2 flex items-center gap-2 text-xs font-semibold transition-colors ${!isSES ? "border-[#0066CC] bg-blue-50 text-[#0066CC] shadow-sm" : "border-slate-200 text-slate-500 bg-white hover:bg-slate-50"}`} data-testid="provider-smtp">
              <Server className="w-4 h-4" />
              <div className="flex-1 text-left">
                <div>SMTP</div>
                <div className="text-[10px] font-normal opacity-70">Gmail / Zoho / Office365 / custom</div>
              </div>
            </button>
            <button type="button" onClick={() => setProvider("aws_ses")} className={`flex-1 border rounded-lg px-3 py-2 flex items-center gap-2 text-xs font-semibold transition-colors ${isSES ? "border-[#0066CC] bg-blue-50 text-[#0066CC] shadow-sm" : "border-slate-200 text-slate-500 bg-white hover:bg-slate-50"}`} data-testid="provider-ses">
              <Cloud className="w-4 h-4" />
              <div className="flex-1 text-left">
                <div>AWS SES</div>
                <div className="text-[10px] font-normal opacity-70">Recommended for absolin.com</div>
              </div>
            </button>
          </div>
        </div>

        {/* SMTP fields */}
        {!isSES && (
          <section className="border rounded-lg p-3 bg-slate-50/60">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2"><Label className="text-[10.5px]">Host *</Label><Input value={form.smtp.host} onChange={e => setSmtp("host", e.target.value)} placeholder="smtp.gmail.com" className="h-9 text-xs" data-testid="smtp-host" /></div>
              <div><Label className="text-[10.5px]">Port</Label><Input type="number" value={form.smtp.port} onChange={e => setSmtp("port", Number(e.target.value))} className="h-9 text-xs" data-testid="smtp-port" /></div>
              <div><Label className="text-[10.5px]">Username</Label><Input value={form.smtp.username} onChange={e => setSmtp("username", e.target.value)} className="h-9 text-xs" data-testid="smtp-username" /></div>
              <div><Label className="text-[10.5px]">Password / App Password</Label><Input type="password" value={form.smtp.password} onChange={e => setSmtp("password", e.target.value)} className="h-9 text-xs" data-testid="smtp-password" /></div>
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2"><Switch checked={!!form.smtp.use_tls} onCheckedChange={v => setSmtp("use_tls", v)} data-testid="smtp-tls" /><span className="text-[11px]">STARTTLS</span></div>
                <div className="flex items-center gap-2"><Switch checked={!!form.smtp.use_ssl} onCheckedChange={v => setSmtp("use_ssl", v)} data-testid="smtp-ssl" /><span className="text-[11px]">SSL</span></div>
              </div>
              <div><Label className="text-[10.5px]">From Email *</Label><Input type="email" value={form.smtp.from_email} onChange={e => setSmtp("from_email", e.target.value)} placeholder="accounts@absolin.com" className="h-9 text-xs" data-testid="smtp-from-email" /></div>
              <div><Label className="text-[10.5px]">From Name</Label><Input value={form.smtp.from_name} onChange={e => setSmtp("from_name", e.target.value)} className="h-9 text-xs" data-testid="smtp-from-name" /></div>
              <div><Label className="text-[10.5px]">Reply-To</Label><Input type="email" value={form.smtp.reply_to} onChange={e => setSmtp("reply_to", e.target.value)} className="h-9 text-xs" data-testid="smtp-reply-to" /></div>
            </div>
          </section>
        )}

        {/* SES fields */}
        {isSES && (
          <section className="border-2 border-blue-100 rounded-lg p-3 bg-blue-50/40">
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="w-4 h-4 text-[#0066CC]" />
              <p className="text-[11px] font-bold text-[#0066CC]">AWS Simple Email Service Configuration</p>
            </div>
            <div className="text-[10.5px] text-slate-600 mb-3 leading-relaxed">
              Create an IAM user in AWS with the <code className="bg-white px-1 py-0.5 rounded border">AmazonSESFullAccess</code> policy attached, generate an access key pair, and verify your sender domain / email in SES → Configuration → Verified identities.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label className="text-[10.5px]">AWS Access Key ID *</Label><Input value={form.ses.aws_access_key_id} onChange={e => setSes("aws_access_key_id", e.target.value)} placeholder="AKIA..." className="h-9 text-xs font-mono" data-testid="ses-access-key" /></div>
              <div className="md:col-span-2"><Label className="text-[10.5px]">AWS Secret Access Key *</Label><Input type="password" value={form.ses.aws_secret_access_key} onChange={e => setSes("aws_secret_access_key", e.target.value)} placeholder="40-char secret" className="h-9 text-xs font-mono" data-testid="ses-secret-key" /></div>
              <div>
                <Label className="text-[10.5px]">AWS Region *</Label>
                <select value={form.ses.region} onChange={e => setSes("region", e.target.value)} className="w-full h-9 border rounded px-2 text-xs bg-white" data-testid="ses-region">
                  {AWS_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div><Label className="text-[10.5px]">Configuration Set (optional)</Label><Input value={form.ses.configuration_set} onChange={e => setSes("configuration_set", e.target.value)} placeholder="absolin-invoices" className="h-9 text-xs" data-testid="ses-config-set" /></div>
              <div><Label className="text-[10.5px]">From Email * (verified in SES)</Label><Input type="email" value={form.ses.from_email} onChange={e => setSes("from_email", e.target.value)} placeholder="accounts@absolin.com" className="h-9 text-xs" data-testid="ses-from-email" /></div>
              <div><Label className="text-[10.5px]">From Name</Label><Input value={form.ses.from_name} onChange={e => setSes("from_name", e.target.value)} className="h-9 text-xs" data-testid="ses-from-name" /></div>
              <div className="md:col-span-2"><Label className="text-[10.5px]">Reply-To</Label><Input type="email" value={form.ses.reply_to} onChange={e => setSes("reply_to", e.target.value)} placeholder="support@absolin.com" className="h-9 text-xs" data-testid="ses-reply-to" /></div>
            </div>
            <div className="mt-3 text-[10px] text-slate-500 bg-white border rounded p-2 leading-relaxed">
              <strong>Note:</strong> If your SES account is in Sandbox mode, only verified recipient addresses will receive email.
              Request production access from AWS console before sending to real customer addresses.
            </div>
          </section>
        )}

        {/* Send test email */}
        <section className="border-t pt-3">
          <Label className="text-[10.5px] text-slate-500 uppercase tracking-wider">Send Test Email</Label>
          <div className="flex gap-2 mt-1">
            <Input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com" className="h-9 text-xs flex-1" data-testid="test-to" />
            <Button onClick={sendTest} disabled={testing} variant="outline" className="gap-2 h-9" data-testid="test-send-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Saves the current config and dispatches a real test email using the selected provider.</p>
        </section>

        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="email-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Email Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
